// ============================================================================
// Evicting the retained reads of `ppp-layer-groups-v1` after it is saved
// ============================================================================
//
// The query defaults keep resolved data for five minutes so a back-navigation
// repaints instantly. That is right for reads and wrong immediately after a
// WRITE to a document six resources project: /layer/groups can report Saved,
// the operator can walk straight to /dailys or Home, and the retained entry
// paints the PRE-SAVE Retirement Home — still active — while the refetch is in
// flight. Updating the view's own state and the legacy LayerContext mirror does
// nothing about that, because neither is what those routes read.
//
// So the entries are REMOVED rather than invalidated. Invalidation marks them
// stale and refetches, but a mounted consumer still renders the retained value
// for the length of that request; removal leaves nothing to render, so the
// revisit shows its loading state and then the truth. That is the whole
// difference the review is about.
//
// Prefix removal by resource id is exactly what `resourceKey` was shaped for:
// its key is [resourceId, scopeSegment, params], and its own comment records
// that a prefix match on [resourceId] selects every scope's entries. So one
// removal per resource covers every subject/role-preview variant, including the
// entries belonging to a role preview the operator is not currently in.
//
// This lives in src/app/data because it touches the Query client. It adds no
// read and no cache framework; it uses the client the app already mounts.
// ============================================================================
import {useCallback} from 'react';
import {useQueryClient} from '@tanstack/react-query';
import type {QueryClient} from '@tanstack/react-query';

import {RESOURCE} from './routeDataCatalog.js';

/**
 * Every resource whose payload is derived from `ppp-layer-groups-v1`.
 *
 * Missing one is invisible until an operator walks to that surface inside the
 * retention window, which is why the list is named here beside the reason
 * rather than inlined at the call site.
 */
export const LAYER_GROUP_BLOB_RESOURCES: readonly string[] = Object.freeze([
  RESOURCE.LAYER_GROUPS_CORE, // /layer/groups itself
  RESOURCE.LAYER_DAILYS_GROUP_DIRECTORY, // /layer/dailys + its record page
  RESOURCE.EGG_DAILYS_GROUPS, // /layer/eggs and the /dailys hub
  RESOURCE.LAYER_RECORD_APP_STORE, // the layer batch/housing record pages
  RESOURCE.HOME_STANDARD_PLANNER, // Home missed reports + all-clear
  RESOURCE.HOME_LIGHT_PLANNER, // the Light portal's missed reports
]);

/**
 * Returns a callback that drops every retained read of the layer-groups blob.
 *
 * Call it AFTER the write is confirmed and BEFORE the visible Saved state: once
 * an operator has seen Saved, no surface may still paint the value that save
 * replaced.
 */
export function evictLayerGroupBlobReads(queryClient: QueryClient): void {
  for (const resourceId of LAYER_GROUP_BLOB_RESOURCES) {
    queryClient.removeQueries({queryKey: [resourceId]});
  }
}

export function useLayerGroupBlobEviction(): () => void {
  const queryClient = useQueryClient();
  return useCallback(() => evictLayerGroupBlobReads(queryClient), [queryClient]);
}
